import { ArrowRightOutlined, SyncOutlined } from "@ant-design/icons";
import { Button, Row, Tag, Tooltip } from "antd";
import { useEffect, useState } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { globalNotificationState, globalUserDetailState } from "../../../_common/global_state";
import { apiGetQuestionnaireFormSetting, apiGetQuestionnaireRecordInit, apiGetQuestionnaireRecordStatus } from "../../../api/restful/questionnaire";

export const QuestionnaireField = ({ formSettingId, label, fieldPath, form, handleAutoSave, layout, ...props }) => {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(false);
    const [initializing, setInitializing] = useState(false);
    const [checkingStatus, setCheckingStatus] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const userDetail = useRecoilValue(globalUserDetailState);
    const setNotificationMessage = useSetRecoilState(globalNotificationState);

    const recordId = form?.getFieldValue(fieldPath);

    useEffect(() => {
        if (recordId) {
            checkStatus();
        }
    }, [recordId]);

    const checkStatus = async () => {
        if (!recordId) return;
        setCheckingStatus(true);
        try {
            const response = await apiGetQuestionnaireRecordStatus(recordId);
            if (response?.status === 'success') {
                setIsSubmitted(response?.data);
            }
        } catch (error) {
            console.error("Failed to check questionnaire status:", error);
        } finally {
            setCheckingStatus(false);
        }
    };

    useEffect(() => {
        const fetchSettings = async () => {
            if (formSettingId) {
                setLoading(true);
                try {
                    const data = await apiGetQuestionnaireFormSetting(formSettingId);
                    if (data?.status === 'success') {
                        setSettings(data?.data);
                    }
                } catch (error) {
                    console.error("Failed to fetch questionnaire settings:", error);
                } finally {
                    setLoading(false);
                }
            }
        };

        fetchSettings();
    }, [formSettingId]);

    const handleInitQuestionnaireRecord = async () => {
        const theme = localStorage.getItem('color');
        const lang = localStorage.getItem('language');
        if (recordId) {
            const url = "https://" + window.location.hostname.replace("member", "questionnaire") + "/#/_public/forms/external/" + recordId + `?theme=${theme}&lang=${lang}`
            window.open(url, '_blank');
            return;
        }

        if (!userDetail?.email) return;

        setInitializing(true);
        try {
            const payload = {
                email: userDetail.email,
                formSetting: formSettingId
            };
            const response = await apiGetQuestionnaireRecordInit(payload);

            if (response?.status === 'success') {
                const recordId = response?.data?._id
                const url = "https://" + window.location.hostname.replace("member", "questionnaire") + "/#/_public/forms/external/" + recordId + `?theme=${theme}&lang=${lang}`

                if (form && fieldPath) {
                    form.setFieldValue(fieldPath, recordId);

                    if (handleAutoSave) {
                        handleAutoSave();
                    }
                }

                window.open(url, '_blank');
            } else {
                setNotificationMessage({
                    status: "error",
                    title: "Error",
                    msg: response?.msg || response?.message || "Failed to initialize questionnaire",
                    key: "questionnaire_init_error",
                });
            }
        } catch (error) {
            console.error("Failed to initialize questionnaire record:", error);
            setNotificationMessage({
                status: "error",
                title: "Error",
                msg: error?.response?.data?.msg || error?.response?.data?.message || error?.message || "Failed to initialize questionnaire",
                key: "questionnaire_init_error",
            });
        } finally {
            setInitializing(false);
        }
    };

    const renderLayout1 = () => {
        return (
            <div className="questionnaire-card">
                <Row justify={"space-between"}>
                    <div>
                        <span className="flex">
                            <h6>{settings?.title}</h6>
                            {
                                recordId && (
                                    <Tag className="ml-2" color={isSubmitted ? "success" : "warning"}>
                                        {isSubmitted ? "Submitted" : "Pending"}
                                    </Tag>
                                )
                            }
                        </span>
                        <p><small>{settings?.description}</small></p>
                    </div>
                    <div className="flex gap-2 items-center">
                        {recordId && !isSubmitted && (
                            <Tooltip title="Check Status">
                                <Button
                                    type="link"
                                    icon={<SyncOutlined spin={checkingStatus} />}
                                    onClick={checkStatus}
                                    loading={checkingStatus}
                                />
                            </Tooltip>
                        )}
                        <Tooltip title="Proceed">
                            <Button
                                type="link"
                                loading={loading || initializing}
                                icon={<ArrowRightOutlined />}
                                onClick={handleInitQuestionnaireRecord}
                            />
                        </Tooltip>
                    </div>
                </Row>
            </div>
        )
    }

    const renderLayout2 = () => {
        const headerImage = settings?.theme?.headerImage;

        return (
            <div className="questionnaire-card" style={{ padding: 0 }}>
                <div style={{ width: '100%', height: '100px', overflow: 'hidden', borderRadius: '8px 8px 0 0', background: 'var(--secondary-card-bg-color)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {headerImage ? (
                        <img src={headerImage} alt="Header" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                        <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" stroke="var(--divider-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M14 2V8H20" stroke="var(--divider-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M16 13H8" stroke="var(--divider-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M16 17H8" stroke="var(--divider-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M10 9H8" stroke="var(--divider-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    )}
                </div>
                <div style={{ padding: '16px' }}>
                    <Row justify={"space-between"}>
                        <div>
                            <span className="flex">
                                <h6>{settings?.title}</h6>
                                {
                                    recordId && (
                                        <Tag className="ml-2" color={isSubmitted ? "success" : "warning"}>
                                            {isSubmitted ? "Submitted" : "Pending"}
                                        </Tag>
                                    )
                                }
                            </span>
                            <p><small>{settings?.description}</small></p>
                        </div>
                        <div className="flex gap-2 items-center">
                            {recordId && !isSubmitted && (
                                <Tooltip title="Check Status">
                                    <Button
                                        type="link"
                                        icon={<SyncOutlined spin={checkingStatus} />}
                                        onClick={checkStatus}
                                        loading={checkingStatus}
                                    />
                                </Tooltip>
                            )}
                            <Tooltip title="Proceed">
                                <Button
                                    className="secondary_btn"
                                    loading={loading || initializing}
                                    icon={<ArrowRightOutlined />}
                                    onClick={handleInitQuestionnaireRecord}
                                />
                            </Tooltip>
                        </div>
                    </Row>
                </div>
            </div>
        )
    }

    const renderLayout3 = () => {
        return (
            <div className="questionnaire-card">
                <Row justify={"space-between"}>
                    <div>
                        <span className="flex">
                            <h6>{settings?.title}</h6>
                            {
                                recordId && (
                                    <Tag className="ml-2" color={isSubmitted ? "success" : "warning"}>
                                        {isSubmitted ? "Submitted" : "Pending"}
                                    </Tag>
                                )
                            }
                        </span>
                        <p><small>{settings?.description}</small></p>
                    </div>
                    <div className="flex gap-2 items-center">
                        {recordId && !isSubmitted && (
                            <Tooltip title="Check Status">
                                <Button
                                    type="link"
                                    icon={<SyncOutlined spin={checkingStatus} />}
                                    onClick={checkStatus}
                                    loading={checkingStatus}
                                />
                            </Tooltip>
                        )}
                        <Tooltip title="Proceed">
                            <Button
                                className="secondary_btn"
                                loading={loading || initializing}
                                icon={<ArrowRightOutlined />}
                                onClick={handleInitQuestionnaireRecord}
                            />
                        </Tooltip>
                    </div>
                </Row>
            </div>
        )
    }

    switch (layout) {
        case "layout_2":
            return renderLayout2();
        case "layout_3":
            return renderLayout3();
        default:
            return renderLayout1();
    }
};
